import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, X, AlertCircle, ArrowLeft, LogIn } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { apiService } from '../services/api';
import { LoginCredentials } from '../types';
import toast from 'react-hot-toast';

interface LoginDialogProps {
  isOpen: boolean;
  onClose: () => void;
  prefillEmail?: string;
  onSuccess: () => void;
}

const LoginDialog: React.FC<LoginDialogProps> = ({
  isOpen,
  onClose,
  prefillEmail = '',
  onSuccess,
}) => {
  const { login, updateUser } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const googleSignInRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<LoginCredentials>();

  // Pre-fill email when dialog opens
  useEffect(() => {
    if (isOpen && prefillEmail) {
      const normalized = prefillEmail.trim().toLowerCase();
      setValue('email', normalized, { shouldValidate: false });
    }
  }, [isOpen, prefillEmail, setValue]);

  const handleGoogleSignIn = useCallback(
    async (response: any) => {
      setIsLoading(true);
      setErrorMessage(null);
      try {
        let credential: string;
        if (typeof response === 'string') {
          credential = response;
        } else if (response?.credential) {
          credential =
            typeof response.credential === 'string'
              ? response.credential
              : response.credential.credential;
        } else {
          throw new Error('No credential received from Google');
        }
        if (!credential || typeof credential !== 'string') {
          throw new Error('Invalid credential format from Google');
        }
        const authResponse = await apiService.googleLogin(credential, 'customer');
        localStorage.setItem('authToken', authResponse.token);
        localStorage.setItem('user', JSON.stringify(authResponse.user));
        updateUser(authResponse.user);
        toast.success('Login successful!');
        onSuccess();
        onClose();
      } catch (error: any) {
        const errorMsg =
          error.response?.data?.message || error.message || 'Google login failed. Please try again.';
        setErrorMessage(errorMsg);
        toast.error(errorMsg);
      } finally {
        setIsLoading(false);
      }
    },
    [updateUser, onSuccess, onClose]
  );

  // Initialize Google Sign-In when dialog is open
  useEffect(() => {
    if (!isOpen) return;
    const initGoogle = () => {
      if ((window as any).google && googleSignInRef.current) {
        (window as any).google.accounts.id.initialize({
          client_id:
            process.env.REACT_APP_GOOGLE_CLIENT_ID ||
            '946690405620-31eg6dhvpvf2mud0t67dtdmo6bjbkpif.apps.googleusercontent.com',
          callback: handleGoogleSignIn,
        });
        (window as any).google.accounts.id.renderButton(googleSignInRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'signin_with',
          width: '100%',
        });
      }
    };
    if ((window as any).google) {
      initGoogle();
    } else {
      const interval = setInterval(() => {
        if ((window as any).google) {
          initGoogle();
          clearInterval(interval);
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isOpen, handleGoogleSignIn]);

  const onSubmit = async (data: LoginCredentials) => {
    setIsLoading(true);
    setErrorMessage(null);
    try {
      const normalizedEmail = data.email.trim().toLowerCase();
      await login({ ...data, email: normalizedEmail, loginType: 'customer' });
      toast.success('Login successful!');
      onSuccess();
      onClose();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || 'Login failed. Please try again.';
      setErrorMessage(errorMsg);
      toast.error(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-modal="true" role="dialog">
      <div className="fixed inset-0 bg-black/50 transition-opacity" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className="relative bg-white rounded-lg shadow-xl max-w-md w-full p-6"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900">Sign in to add your business</h3>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-1 rounded"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div>
              <label htmlFor="login-dialog-email" className="block text-sm font-medium text-gray-700 mb-1">
                Email address
              </label>
              <input
                id="login-dialog-email"
                type="email"
                autoComplete="email"
                disabled
                className={`aacore-textfield-primary w-full bg-gray-50 cursor-not-allowed ${errors.email ? 'border-red-500' : ''}`}
                placeholder="Enter your email"
                {...register('email', {
                  required: 'Email is required',
                  pattern: {
                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                    message: 'Invalid email address',
                  },
                  onChange: (e) => {
                    const v = e.target.value.toLowerCase();
                    e.target.value = v;
                    setValue('email', v, { shouldValidate: false });
                  },
                })}
              />
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="login-dialog-password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  id="login-dialog-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  className={`aacore-textfield-primary w-full pr-10 ${errors.password ? 'border-red-500' : ''}`}
                  placeholder="Enter your password"
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 6, message: 'Password must be at least 6 characters' },
                  })}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
              )}
            </div>

            {errorMessage && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="aacore-btn-outlined flex-1 justify-center inline-flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" aria-hidden />
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="aacore-btn-primary flex-1 justify-center inline-flex items-center gap-2"
              >
                {isLoading ? (
                  <span className="inline-flex items-center justify-center gap-2">
                    <span className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                    Signing in...
                  </span>
                ) : (
                  <>
                    <LogIn className="h-4 w-4" aria-hidden />
                    Sign in
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-4">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Or continue with</span>
              </div>
            </div>
            <div
              ref={googleSignInRef}
              className="mt-3 w-full flex justify-center"
              style={{ minHeight: '40px' }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginDialog;
